const DEFAULT_API_BASE = 'https://picnic-basket-api.dont-d23.workers.dev';
const API_BASE = import.meta.env.VITE_API_BASE?.replace(/\/$/, '') || DEFAULT_API_BASE;
export class ApiError extends Error {
    constructor(message, options) {
        super(message);
        this.name = 'ApiError';
        this.status = options.status;
        this.reason = options.reason;
        this.retryAfterSeconds = options.retryAfterSeconds;
        this.retryAt = options.retryAt;
        this.detail = options.detail;
    }
}
async function parseJson(response) {
    const text = await response.text();
    if (!text)
        return {};
    try {
        return JSON.parse(text);
    }
    catch {
        return {};
    }
}
async function api(path, init = {}) {
    const res = await fetch(`${API_BASE}${path}`, init);
    const data = await parseJson(res);
    if (!res.ok) {
        const errorText = String(data?.error || 'Error');
        const detailText = typeof data?.detail === 'string' ? data.detail : undefined;
        const reason = typeof data?.reason === 'string' ? data.reason : undefined;
        const retryAfterSeconds = Number.isFinite(Number(data?.retryAfterSeconds)) ? Number(data.retryAfterSeconds) : undefined;
        const retryAt = Number.isFinite(Number(data?.retryAt)) ? Number(data.retryAt) : undefined;
        const message = detailText ? `${errorText}: ${detailText}` : (errorText || `HTTP ${res.status}`);
        throw new ApiError(message, {
            status: res.status,
            reason,
            retryAfterSeconds,
            retryAt,
            detail: detailText,
        });
    }
    return data;
}
export async function loginWithEmail(email) {
    const data = await api('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, deviceLabel: 'Picnic Basket Web' }),
    });
    return { user: data.user, session: normalizeSession(data.session) };
}
export function getWorkOsStartUrl(email, returnTo) {
    const url = new URL(`${API_BASE}/api/auth/workos/start`);
    if (email.trim())
        url.searchParams.set('email', email.trim());
    if (returnTo.trim())
        url.searchParams.set('return_to', returnTo.trim());
    return url.toString();
}
export async function verifyWorkOsCode(code) {
    const data = await api('/api/auth/workos/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, deviceLabel: 'Picnic Basket Web' }),
    });
    return { user: data.user, session: normalizeSession(data.session) };
}
export async function redeemQrToken(qrToken) {
    const data = await api('/api/auth/qr/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qrToken, deviceLabel: 'Picnic Basket Web' }),
    });
    return { user: data.user, session: normalizeSession(data.session) };
}
export async function refreshAccessToken(refreshToken) {
    const data = await api('/api/auth/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
    });
    return { accessToken: String(data.accessToken), expiresIn: Number(data.expiresIn || 900) };
}
export async function fetchAccount(accessToken) {
    const data = await api('/api/auth/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    return {
        user: data.user,
        runtimes: Array.isArray(data.runtimes) ? data.runtimes : [],
        entitled: typeof data.entitled === 'boolean' ? data.entitled : undefined,
        subscription: data.subscription,
        usage: data.usage,
    };
}
export async function fetchBillingState(accessToken, refresh = false) {
    const data = await api(`/api/billing/state${refresh ? '?refresh=1' : ''}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    return {
        entitled: Boolean(data.entitled),
        subscription: data.subscription,
        usage: data.usage,
    };
}
export async function createBillingCheckout(accessToken) {
    const data = await api('/api/billing/checkout', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            returnUrl: `${window.location.origin}/chat`,
            successUrl: `${window.location.origin}/chat?subscribed=1`,
        }),
    });
    return { checkoutUrl: String(data.checkoutUrl || '') };
}
export async function createBillingPortal(accessToken) {
    const data = await api('/api/billing/portal', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });
    return { customerPortalUrl: String(data.customerPortalUrl || '') };
}
export async function fetchChatHistory(accessToken, threadId) {
    const params = new URLSearchParams({ threadId });
    const data = await api(`/api/chat/history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    return Array.isArray(data.messages) ? data.messages : [];
}
export async function sendChatMessage(accessToken, threadId, text, attachments) {
    await api('/api/chat/send', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ threadId, text, attachments }),
    });
}
export async function uploadAttachment(accessToken, file, threadId) {
    const create = await api('/api/files/upload-url', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
        }),
    });
    const uploadUrl = String(create.uploadUrl || '');
    const attachmentId = String(create.attachmentId || '');
    const key = String(create.key || '');
    if (!uploadUrl || !attachmentId || !key) {
        throw new Error('Attachment upload initialization failed.');
    }
    const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
    });
    if (!putRes.ok) {
        throw new Error(`Attachment upload failed (${putRes.status}).`);
    }
    await api('/api/files/attach', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ attachmentId, threadId }),
    });
    return {
        attachmentId,
        key,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
    };
}
export async function fetchDeviceStatus(accessToken) {
    const data = await api('/api/device/status', {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    return Array.isArray(data.runtimes) ? data.runtimes : [];
}
function normalizeSession(raw) {
    return {
        accessToken: String(raw?.accessToken || ''),
        refreshToken: String(raw?.refreshToken || ''),
        expiresIn: Number(raw?.expiresIn || 900),
        refreshExpiresIn: Number(raw?.refreshExpiresIn || 2592000),
    };
}
